<template lang="pug">
.sharing-buttons
  | {{ __('Share') }}
  button.share.a11y(
    v-for="service in SERVICES"
    @click="share(service)"
    :aria-label="'Share on '+service.name"
    v-html="service.iconHtml"
    :class="service.name"
  )
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'

const rogue = useRogue()

type ShareData = {
  url: string,
  text: string,
  image: string,
  title: string,
}

type Service = {
  baseLink: string,
  name : string,
  windowParams : string,
  tooltip : string,
  iconHtml: string,
  urlParams: (options: ShareData) => object,
}

const SERVICES: Service[] = [
  {
    baseLink     : 'https://www.facebook.com/sharer/sharer.php?',
    urlParams    : options => ({ u: options.url }),
    name         : 'facebook',
    windowParams : 'width=580,height=296',
    tooltip      : 'Share on Facebook',
    iconHtml     : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M23.7.2V5H21c-1 0-1.7.2-2 .7s-.6 1-.6 1.9v3.4h5.3l-.7 5.3h-4.6v13.5H13V16.2H8.3v-5.3H13v-4c0-2.1.6-3.9 1.8-5s3-1.9 5-1.9c1.7 0 3 0 4 .2z"/></svg>',
  },
  {
    baseLink     : 'https://twitter.com/intent/tweet?',
    urlParams    : options => ({ text: options.text, url: options.url }),
    name         : 'twitter',
    windowParams : 'width=550,height=270',
    tooltip      : 'Share on Twitter',
    iconHtml     : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/></svg>',
  },
  {
    baseLink     : 'https://pinterest.com/pin/create/button/?',
    urlParams    : options => ({ url: options.url, media: options.image, description: options.title }),
    name         : 'pinterest',
    windowParams : '',
    tooltip      : 'Share on Pinterest',
    iconHtml     : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M4.6 10.7q0-2 .6-3.7t1.9-3T9.8 2 13.1.5t3.6-.5q2.8 0 5.3 1.2t4 3.4 1.4 5.2q0 1.7-.3 3.3t-1 3.2-1.9 2.7-2.6 1.8-3.3.7q-1.2 0-2.4-.6T14 19.3l-.5 2-.4 1.7-.3 1.3-.5 1.3-.6 1.1-.8 1.4-1.1 1.5-.3.1-.1-.2-.3-3.3q0-1.7.4-3.7t1.2-5.1 1-3.7q-.7-1.1-.7-3 0-1.5 1-2.8t2.3-1.3q1.1 0 1.7.7t.6 1.9q0 1.2-.8 3.4t-.7 3.3q0 1.2.8 1.9t2 .7q.9 0 1.7-.4t1.4-1.2 1-1.7.7-2 .4-2 0-1.8q0-3-1.9-4.8t-5-1.7q-3.6 0-6 2.3t-2.4 5.9q0 .8.2 1.5t.5 1.2.5.8.2.5q0 .5-.3 1.3t-.6.8H8q-1-.3-1.7-1t-1-1.7-.6-2-.2-1.8z"/></svg>',
  },
  {
    baseLink     : 'mailto:?',
    urlParams    : options => ({ subject: options.title, body: `${options.text} ${options.url}` }),
    windowParams : '',
    name         : 'email',
    tooltip      : 'Share via email',
    iconHtml     : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M28 24.5v-12l-1 1a223 223 0 00-8 6.3l-1.4.8a4 4 0 01-1.6.4 4 4 0 01-1.6-.4l-1.4-.8-1.2-1A218.5 218.5 0 004 12.5v12s0 .2.2.3l.3.2h23c.1 0 .3 0 .4-.2l.1-.3zM28 8v-.7l-.1-.2h-.2l-.2-.1h-23l-.3.1-.2.4c0 1.7.8 3.2 2.3 4.4a2136.5 2136.5 0 019 7h1.4l.8-.5a16.2 16.2 0 002-1.5l6.2-5c.6-.4 1-1 1.6-1.8.5-.8.7-1.4.7-2zm2-.5v17c0 .6-.2 1.2-.7 1.7s-1.1.8-1.8.8h-23c-.7 0-1.3-.3-1.7-.8s-.8-1-.8-1.7v-17c0-.7.3-1.3.8-1.8s1-.7 1.7-.7h23c.7 0 1.3.2 1.8.7s.7 1 .7 1.8z"/></svg>',
  },
]

const props = defineProps({
  shareData: {
    type     : Object as PropType<ShareData>,
    required : false,
  },
  productId: {
    type     : Number,
    required : false,
  },
})

const productStore = useProductStore()

const products = computed(() => productStore.products)

function share(service: Service) {
  const data = Object.assign({
    title : document.title,
    text  : document.querySelector('meta[name="description"]')?.content || '',
    url   : window.location,
    image : document.querySelector('meta[property="og:image"]')?.content || '',
  }, props.shareData)

  const params = Object.entries(service.urlParams(data)).map(([key, val]) => `${key}=${val}`).join('&')
  const url = service.baseLink + params

  window.open(encodeURI(url), '_blank', service.windowParams)
  if (props.productId) {
    const product = products.value[props.productId]
    rogue.track.track({ type: 'shareProduct', service, product })
  }
}
</script>

<style lang="scss" scoped>
.sharing-buttons {
  font-size: $size-xs;
  display: flex;
  font-weight: $weight-bold;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin: $space-s 0;
  min-height: 32px;

  &.left {
    justify-content: flex-start;
  }

  .share {
    &:focus {
      outline: $color-blue-dark 2px auto;
    }
    height: 24px;
    width: 24px;
    cursor: pointer;
    display: inline-block;
    margin: 0 $space-xs;

    &:first-child {
      margin-left: $space-m;
    }

    :deep(svg) {
      transition: margin-top ease 300ms;
    }
    &:hover :deep(svg) {
      margin-top: -4px;
    }
  }
}
</style>
